import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Gavel, Youtube, Mail, BookOpen, Send, Loader2, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, client, getToken } from "@/lib/api";
import { trackEvent } from "@/lib/ga4";
import AccessibilityBadge from "@/components/AccessibilityBadge";
import FooterAppPromo from "@/components/FooterAppPromo";
import TextSizeControl from "@/components/TextSizeControl";
import { LanguageBar } from "@/components/LanguageWidget";

// Fallback caso a API de podcasts falhe — a lista real vem do banco
// (podcasts visíveis, com episódios publicados e playlist no YouTube).
const PLAYLISTS_FALLBACK = [
  { name: "Diálogos de Direito Administrativo", url: "https://www.youtube.com/playlist?list=PL8XZVSdhHJuS2RpNTjxqUruizZ-ZBvXqz" },
  { name: "Jurisprudência em Debate", url: "https://www.youtube.com/playlist?list=PL8XZVSdhHJuSx-ZvXAEx5jKvN6zuHOevT" },
  { name: "República em Cena", url: "https://www.youtube.com/playlist?list=PL8XZVSdhHJuQI_mayN3VME_6uFNEN-F5f" },
  { name: "Palestras de Direito Administrativo", url: "https://www.youtube.com/playlist?list=PL8XZVSdhHJuS7tIi4k2NfLjzyrISzcIEX" },
  { name: "Encontros de Direito Administrativo", url: "https://www.youtube.com/playlist?list=PL8XZVSdhHJuRsMObZZLV0OrrIiq_SF3UR" },
  { name: "Observatório da Reforma Administrativa", url: "https://www.youtube.com/playlist?list=PL8XZVSdhHJuSX7OQhd3QCrMyYbEyaf-td" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // {type: 'success'|'already'|'error', msg: string}
  const [playlists, setPlaylists] = useState(PLAYLISTS_FALLBACK);

  // Lista de playlists vem do banco: só podcasts visíveis (não ocultos),
  // COM episódios publicados e COM playlist no YouTube. Assim a
  // Jurisprudência Internacional aparece e os podcasts sem episódio
  // (ou ocultos) ficam de fora — sem manutenção manual.
  useEffect(() => {
    let cancelled = false;
    client.get("/podcasts")
      .then((r) => {
        const rows = (r.data || [])
          .filter((p) => (p.episode_count || 0) > 0 && p.playlist_url)
          .map((p) => ({ name: p.name, url: p.playlist_url }));
        if (!cancelled && rows.length) setPlaylists(rows);
      })
      .catch(() => { /* mantém o fallback */ });
    return () => { cancelled = true; };
  }, []);
  // Only show "Área do gestor" link if admin is already logged in
  const isAdmin = !!getToken();
  // Hide the giant newsletter band on the homepage — the form lives on
  // every inner page already, and the editor asked to keep the home
  // shorter so first-load impression isn't a giant signup blob.
  const { pathname } = useLocation();
  const showNewsletterBand = pathname !== "/";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await api.subscribe(email.trim());
      if (res.already_subscribed) {
        setStatus({ type: "already", msg: "Este e-mail já estava inscrito. Tudo certo!" });
        toast.info("Você já está inscrito!");
        trackEvent("newsletter_subscribe", { source: "footer", outcome: "already_subscribed" });
      } else if (res.pending) {
        setStatus({ type: "pending", msg: "Enviamos um e-mail de confirmação. Abra sua caixa de entrada (e o lixo eletrônico) e clique em 'Confirmar inscrição'." });
        toast.success("Confirme no seu e-mail!");
        trackEvent("newsletter_subscribe", { source: "footer", outcome: "pending_confirmation" });
      } else {
        setStatus({ type: "success", msg: "Inscrição confirmada. Você será avisado de novos episódios." });
        toast.success("Inscrição confirmada!");
        trackEvent("newsletter_subscribe", { source: "footer", outcome: "confirmed" });
      }
      setEmail("");
      setTimeout(() => setStatus((s) => (s && s.type !== "error" ? null : s)), 12000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao inscrever. Tente novamente.";
      setStatus({ type: "error", msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative z-10 border-t border-white/5 bg-slate-950/90 mt-20">
      {/* Newsletter band — hidden on the homepage to keep first-load short */}
      {showNewsletterBand && (
      <div className="border-b border-white/5 bg-gradient-to-r from-slate-950 via-slate-900/60 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 py-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6 space-y-2">
            <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-red-400">Boletim do JurisTube</span>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-white">
              Receba automaticamente as novidades do site
            </h3>
            <p className="text-sm text-slate-400 max-w-xl">
              Vídeos, notícias, legislação publicada: direto no seu e-mail, para a sua atualização imediata. Sem spam, cancele quando quiser.
            </p>
          </div>
          <div className="md:col-span-6 space-y-3">
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form">
              <div className="flex-1 flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-full px-4 py-3 focus-within:border-red-500/60 transition">
                <Mail className="w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com.br"
                  className="bg-transparent outline-none flex-1 text-slate-100 text-sm placeholder:text-slate-500"
                  data-testid="newsletter-email-input"
                />
              </div>
              <button type="submit" disabled={loading || !email} className="btn-primary disabled:opacity-50" data-testid="newsletter-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Inscrever-me
              </button>
            </form>
            {status && (
              <div
                role="status"
                aria-live="polite"
                className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
                  status.type === "success" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                  : status.type === "already" ? "border-sky-500/40 bg-sky-500/10 text-sky-200"
                  : status.type === "pending" ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                  : "border-red-500/50 bg-red-500/10 text-red-200"
                }`}
                data-testid="newsletter-feedback"
              >
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="leading-snug">{status.msg}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 pt-16 pb-8 md:pb-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* LEFT COLUMN — brand + playlists no topo, imagem promocional do
            App (Peça B) embaixo. A arte já contém título/sub-texto/CTA
            embutidos, então o wrapper só aplica uma linha âmbar fina
            como separador. Isso preenche o "buraco" vertical que
            existia ao lado direito da lista vertical de ``Navegar``,
            sem criar mais um banner inferior.

            Iter150 — alinhamento pela base. O grid-col-9/12 estica o
            container para a altura do col-3 ("Navegar") da direita,
            que cresceu bastante. Em vez de empilhar com gap fixo,
            usamos flex-col + mt-auto na imagem promocional, jogando-a
            no rodapé do espaço — assim a base da imagem alinha com o
            último item da lista de navegação no desktop. */}
        <div className="md:col-span-8 space-y-10 md:flex md:flex-col md:space-y-0 md:gap-10">
          <div className="grid grid-cols-1 md:grid-cols-9 gap-10">
            <div className="md:col-span-4 space-y-4">
              <div className="flex items-center gap-2">
                <Gavel className="w-6 h-6 text-red-500" />
                <span className="font-serif text-2xl font-bold">Juris<span className="text-red-500">tube</span></span>
              </div>
              <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
                JurisTube: infraestrutura viva de aprendizado e pesquisa em Direito Público. Podcasts, artigos, normas, notícias, vídeos, ferramentas interativas e curadoria científica em um só lugar.
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <BookOpen className="w-3.5 h-3.5" /> Conteúdo acadêmico em português (BR)
              </div>
              <Link
                to={isAdmin ? "/admin" : "/admin/login"}
                className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-sky-400 transition group"
                data-testid="footer-gestor-link"
                title={isAdmin ? "Painel do Gestor" : "Acessar área do Gestor"}
              >
                <Lock className="w-3 h-3 group-hover:scale-110 transition" /> Gestor
              </Link>
            </div>

            <div className="md:col-span-5">
              <h4 className="text-xs uppercase tracking-widest text-sky-400 font-semibold mb-4">Playlists no YouTube</h4>
              <ul className="space-y-2">
                {playlists.map((p) => (
                  <li key={p.url}>
                    <a
                      href={p.url}
                      target="_blank" rel="noreferrer noopener"
                      data-testid={`footer-playlist-${p.name}`}
                      className="group inline-flex items-start gap-2 text-sm text-slate-300 hover:text-red-400 transition"
                    >
                      <Youtube className="w-4 h-4 mt-0.5 text-red-500 shrink-0" />
                      <span>{p.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Peça B (arte completa) com linha âmbar fina como separador.
              Auto-some pra quem já está em standalone. ``md:mt-auto``
              empurra a imagem pra base do container, alinhando com o
              último item de "Navegar" à direita. */}
          <div className="md:mt-auto">
            <FooterAppPromo />
          </div>
        </div>

        <div className="md:col-span-4 space-y-4">
          <h4 className="text-xs uppercase tracking-widest text-sky-400 font-semibold">Navegar</h4>
          <ul className="space-y-2 text-sm text-slate-300 md:columns-2 md:gap-x-6 md:[column-fill:balance] [&>li]:break-inside-avoid">
            <li><Link to="/" className="hover:text-white">Início</Link></li>
            <li><Link to="/conta" className="hover:text-white" data-testid="footer-login-link">Login / Minha conta</Link></li>
            <li><Link to="/busca" className="hover:text-white" data-testid="footer-search-link">Busca Geral</Link></li>
            <li><Link to="/temas" className="hover:text-white">Mapa de Temas</Link></li>
            <li><Link to="/podcasts-de-video" className="hover:text-white" data-testid="footer-podcasts-de-video-link">Podcasts de Vídeo</Link></li>
            <li><a href="#autores" className="hover:text-white">Autores</a></li>
            <li><Link to="/colunistas" className="hover:text-white" data-testid="footer-colunistas-link">Artigos</Link></li>
            <li><Link to="/blog" className="hover:text-white" data-testid="footer-blog-link">Blog</Link></li>
            <li><Link to="/radar-constitucional" className="hover:text-white" data-testid="footer-radar-constitucional-link">Radar Constitucional</Link></li>
            <li><Link to="/noticias-juridicas" className="hover:text-white" data-testid="footer-legal-news-link">Radar de Notícias</Link></li>
            <li><Link to="/noticias-legislativas" className="hover:text-white" data-testid="footer-legal-acts-link">Radar Normativo</Link></li>
            <li><Link to="/dialogos-doutrinarios" className="hover:text-white" data-testid="footer-dialogues-link">Radar Doutrinário</Link></li>
            <li><Link to="/radar-concursos" className="hover:text-white" data-testid="footer-radar-concursos-link">Radar de Concursos</Link></li>
            <li><Link to="/radio" className="hover:text-white" data-testid="footer-radio-link">Rádio JurisTube</Link></li>
            <li><Link to="/livraria" className="hover:text-white" data-testid="footer-livraria-link">Livraria</Link></li>
            <li><Link to="/cursos" rel="nofollow" className="hover:text-white" data-testid="footer-cursos-link">Cursos Exclusivos</Link></li>
            <li><Link to="/comunidade" className="hover:text-white" data-testid="footer-comunidade-link">Comunidade</Link></li>
            <li><Link to="/tv" className="hover:text-white" data-testid="footer-tv-link">TV JurisTube</Link></li>
            <li><Link to="/fototeca" className="hover:text-white" data-testid="footer-fototeca-link">Fototeca</Link></li>
            <li><Link to="/parcerias" className="hover:text-white" data-testid="footer-parcerias-link">Parcerias</Link></li>
            <li><Link to="/app" className="hover:text-white" data-testid="footer-app-link">App no Celular</Link></li>
            <li><Link to="/sobre-colecoes" className="hover:text-white" data-testid="footer-sobre-colecoes-link">Coleções para turmas</Link></li>
            <li><Link to="/guia-professores" className="hover:text-white" data-testid="footer-guia-professores-link">Guia Professores(as)</Link></li>
            <li><Link to="/como-citar" className="hover:text-white" data-testid="footer-como-citar-link">Como citar</Link></li>
            <li><Link to="/bastidores-tecnicos" className="hover:text-white" data-testid="footer-bastidores-link">Bastidores Técnicos</Link></li>
            <li><Link to="/sobre" className="hover:text-white" data-testid="footer-about-link">Sobre o JurisTube</Link></li>
          </ul>

          {/* Iter264 — Feeds RSS/Atom visíveis publicamente.
              Complementa o autodiscovery (Iter263) — Zotero/Feedly já
              detectam via <link rel="alternate"> no <head>, mas o(a)
              leitor(a) comum sem extensão precisa VER os endereços
              para copiar+colar no aggregator dele. Bloco discreto
              (mesmo peso visual do menu "Navegar" ao lado) para não
              competir com os CTAs principais. */}
          <div className="pt-4 mt-4 border-t border-slate-800/70" data-testid="footer-feeds-block">
            <h5 className="text-[11px] uppercase tracking-widest text-amber-400/80 font-semibold mb-2">
              Assine nossos feeds
            </h5>
            <ul className="space-y-1.5 text-[12px] text-slate-400">
              <li>
                <a
                  href="/api/feed/pdfs-cientificos.xml"
                  className="hover:text-amber-300 transition inline-flex items-center gap-1.5"
                  data-testid="footer-feed-pdfs-cientificos"
                >
                  <span aria-hidden>🔬</span> PDFs Científicos (Zotero/Mendeley)
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-slate-950 text-[9px] font-bold uppercase tracking-widest">Novo</span>
                </a>
              </li>
              <li>
                <a
                  href="/api/feed/cientifico.xml"
                  className="hover:text-amber-300 transition inline-flex items-center gap-1.5"
                  data-testid="footer-feed-cientifico"
                >
                  <span aria-hidden>📖</span> Científico (doutrinário)
                </a>
              </li>
              <li>
                <a
                  href="/api/feed/atualidades.xml"
                  className="hover:text-amber-300 transition inline-flex items-center gap-1.5"
                  data-testid="footer-feed-atualidades"
                >
                  <span aria-hidden>📰</span> Atualidades jurídicas
                </a>
              </li>
              <li>
                <a
                  href="/api/feed/scholar.xml"
                  className="hover:text-amber-300 transition inline-flex items-center gap-1.5"
                  data-testid="footer-feed-scholar"
                >
                  <span aria-hidden>🎓</span> Acervo acadêmico (Scholar)
                </a>
              </li>
              <li>
                <a
                  href="/api/rss.xml"
                  className="hover:text-amber-300 transition inline-flex items-center gap-1.5"
                  data-testid="footer-feed-rss"
                >
                  <span aria-hidden>📡</span> RSS de novidades
                </a>
              </li>
              <li className="pt-1.5">
                <Link
                  to="/feeds"
                  className="text-amber-300 hover:text-amber-200 transition inline-flex items-center gap-1 font-semibold"
                  data-testid="footer-feeds-guide"
                >
                  Guia completo dos feeds →
                </Link>
              </li>
            </ul>
          </div>
          {isAdmin && (
            <div className="pt-4 border-t border-slate-800">
              <span className="inline-flex items-center gap-2 text-xs text-sky-400/80" data-testid="footer-admin-session">
                <Lock className="w-3.5 h-3.5" /> Sessão de gestor ativa
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 py-5 text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span>
            © {new Date().getFullYear()} Juristube, IBDP. Todos os direitos reservados.
          </span>
          <span className="text-slate-700">·</span>
          <span>
            Editor:{" "}
            <a
              href="https://linktr.ee/paulomodesto"
              target="_blank"
              rel="noreferrer noopener"
              className="text-sky-400 hover:text-sky-300 transition"
              data-testid="footer-editor-link"
            >
              Prof. Paulo Modesto (UFBA)
            </a>
          </span>
          <span className="text-slate-700">·</span>
          <Link to="/politica-de-privacidade" className="hover:text-slate-300 transition" data-testid="footer-privacy-link">
            Política de privacidade
          </Link>
          <span className="text-slate-700">·</span>
          <Link to="/meus-dados" className="hover:text-slate-300 transition" data-testid="footer-my-data-link">
            Meus dados (LGPD)
          </Link>
          <span className="text-slate-700">·</span>
          {/* Selo de acessibilidade (variante footer) — abre popover
              acima do botão (placement=top) para não ser cortado pela
              borda inferior da viewport. */}
          <AccessibilityBadge variant="footer" placement="top" />
        </div>

        {/* Iter179 — Controle de tamanho de texto na barra final do
            footer. Segundo lugar de acesso rápido (o principal fica
            registrado na página /acessibilidade). Iter189 — ao lado
            dele, uma segunda "âncora" de tradução (bandeiras maiores)
            para usuário idoso/baixa visão que pode não notar o widget
            flutuante da lateral esquerda. Iter265 — no mobile a linha
            fica alinhada à esquerda (justify-start) e cola no rodapé
            (mt-1 pt-2) para eliminar o "buraco" abaixo da imagem do
            App; no desktop segue centralizada e com respiro maior. */}
        <div className="mt-1 pt-2 md:mt-4 md:pt-4 border-t border-slate-800/50 flex items-center justify-start md:justify-center gap-3 md:gap-4 flex-wrap px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 pb-4 md:pb-0" data-testid="footer-text-size">
          <TextSizeControl testIdPrefix="footer-text-size" />
          <span className="hidden md:inline text-slate-700">·</span>
          <LanguageBar testIdPrefix="footer-language" />
        </div>
      </div>
    </footer>
  );
}
